/**
 * VirtualizedImageList - 仮想化された画像リスト表示コンポーネント
 */

import { useRef, useLayoutEffect, useEffect, useState } from 'react'
import { ImageItemWithQuery } from './ImageItemWithQuery'
import { PanoramaViewer } from './PanoramaViewer'
import type { SetItem } from '../../../core/types'
import { logger } from '../../../lib/logger'

// スクロールバーを非表示にするCSS
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`

interface VirtualizedImageListProps {
  items: SetItem[]
  currentIndex: number
  setType?: 'linear' | 'circular'
  currentSet?: { type: 'linear' | 'circular'; id: string; name: string }
}

export function VirtualizedImageList({
  items,
  currentIndex,
  setType,
  currentSet
}: VirtualizedImageListProps) {
  // currentIndexの範囲を安全な値に制限（items配列が有効な場合のみ）
  const safeCurrentIndex = items && items.length > 0 
    ? Math.max(0, Math.min(currentIndex ?? 0, items.length - 1))
    : 0
  
  // ★★★ IMPORTANT: All hooks must be called before any conditional returns ★★★
  const parentRef = useRef<HTMLDivElement>(null)
  const isInitializedRef = useRef(false) // 初期スクロール設定済みフラグ
  const [isPortrait, setIsPortrait] = useState(false)
  
  // Store methods - removed as they're no longer needed for swipe
  // const nextImage = useStore(state => state.nextImage)
  // const previousImage = useStore(state => state.previousImage)
  
  // itemsの長さをメモ化して依存配列の安定性を保つ
  const itemsLength = items?.length ?? 0
  
  // スタイルをheadに追加
  useLayoutEffect(() => {
    const styleId = 'virtualized-image-list-styles'
    let styleElement = document.getElementById(styleId)
    
    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      styleElement.textContent = scrollbarHideStyles
      document.head.appendChild(styleElement)
    }
    
    return () => {
      // コンポーネントがアンマウントされたらスタイルを削除
      const element = document.getElementById(styleId)
      if (element) {
        element.remove()
      }
    }
  }, [])
  
  // 初回マウント時のみスクロール位置を設定
  useLayoutEffect(() => {
    // 既に初期化済みの場合は何もしない
    if (isInitializedRef.current) return
    
    // 次のフレームまで待って実行
    const rafId = requestAnimationFrame(() => {
      if (!parentRef.current) return
      if (!items || items.length === 0) return
      if (isInitializedRef.current) return // 再度チェック
      
      const itemWidth = window.innerWidth
      const targetScrollLeft = safeCurrentIndex * itemWidth
      
      logger.log('[VirtualizedImageList] Initial mount scroll position:', {
        initialIndex: safeCurrentIndex,
        itemWidth,
        targetScrollLeft
      })
      
      // 初回のみ位置を設定
      parentRef.current.scrollLeft = targetScrollLeft
      isInitializedRef.current = true // 初期化完了フラグを立てる
    })
    
    return () => {
      cancelAnimationFrame(rafId)
    }
  }, []) // 空の依存配列で初回マウント時のみ実行
  
  // 画面の向きを検出
  useEffect(() => {
    const checkOrientation = () => {
      const isPortraitMode = window.innerHeight > window.innerWidth
      setIsPortrait(isPortraitMode)
      logger.log('[VirtualizedImageList] Orientation:', isPortraitMode ? 'portrait' : 'landscape')
    }
    
    checkOrientation()
    window.addEventListener('resize', checkOrientation)
    window.addEventListener('orientationchange', checkOrientation)
    
    return () => {
      window.removeEventListener('resize', checkOrientation)
      window.removeEventListener('orientationchange', checkOrientation)
    }
  }, [])
  
  // currentIndexの変更に応じてスクロール位置を更新（一方向データフロー）
  useEffect(() => {
    logger.log('[VirtualizedImageList] useEffect triggered - safeCurrentIndex:', safeCurrentIndex, 'isInitialized:', isInitializedRef.current)
    
    // 初期化が完了していない場合はスキップ
    if (!isInitializedRef.current) {
      logger.log('[VirtualizedImageList] Skipping update - not initialized')
      return
    }
    if (!parentRef.current || !items || items.length === 0) {
      logger.log('[VirtualizedImageList] Skipping update - missing requirements')
      return
    }
    
    const itemWidth = window.innerWidth
    const targetScrollLeft = safeCurrentIndex * itemWidth
    
    logger.log('[VirtualizedImageList] Updating scroll position for currentIndex:', {
      currentIndex: safeCurrentIndex,
      targetScrollLeft,
      itemWidth
    })
    
    // スムーズなアニメーションで位置を更新
    parentRef.current.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    })
  }, [safeCurrentIndex, itemsLength]) // itemsLengthも依存配列に追加
  
  // パノラマモードの判定
  const isPanoramaMode = setType === 'circular' && isPortrait

  // ★★★ 防御的プログラミング - 最初にPropsの妥当性を検証 ★★★
  // This check comes AFTER all hooks are called
  if (!items || !Array.isArray(items) || items.length === 0) {
    logger.warn('[VirtualizedImageList] Invalid or empty items prop received:', items)
    return (
      <div className="flex items-center justify-center h-full bg-black">
        <div className="text-gray-500 text-center">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>画像がありません</p>
        </div>
      </div>
    )
  }

  // パノラマモードの場合はPanoramaViewerを使用
  if (isPanoramaMode) {
    return (
      <PanoramaViewer
        items={items}
        currentIndex={safeCurrentIndex}
        isPortrait={isPortrait}
      />
    )
  }

  // 通常モード
  return (
    <div 
      className="absolute inset-0 bg-black"
    >
      {/* 仮想化スクロールコンテナ */}
      <div
        ref={parentRef}
        className="w-full h-full overflow-x-auto overflow-y-hidden scrollbar-hide"
        style={{
          WebkitOverflowScrolling: 'touch',
          // スクロール自体を無効化
          overflowX: 'hidden'
        }}
      >
        {/* シンプルな実装 - 全アイテムをレンダリング */}
        <div
          className="h-full flex"
          style={{
            width: `${items.length * 100}vw`,
            // スムーズなトランジション
            transition: 'none'
          }}
        >
          {/* 全アイテムを表示 */}
          {items.map((item, index) => (
            <div
              key={item.id}
              className="flex items-center justify-center snap-center"
              style={{
                width: '100vw',
                height: '100%',
                flexShrink: 0
              }}
            >
              <ImageItemWithQuery
                item={item}
                width={1024}
                height={768}
                adjacentItems={{
                  prev: index > 0 ? items[index - 1] : undefined,
                  next: index < items.length - 1 ? items[index + 1] : undefined
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* インデックス表示（currentIndexを使用） */}
      <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm pointer-events-none">
        {safeCurrentIndex + 1} / {items.length}
      </div>
      
      {/* パノラマモード切り替えボタン（circularタイプの場合のみ） */}
      {currentSet?.type === 'circular' && (
        <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
          <button
            onClick={() => setIsPortrait(!isPortrait)}
            className="flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            パノラマモード
          </button>
        </div>
      )}
    </div>
  )
}